import { storage } from "../features/shared/api/storage";

export interface LocaleDefinition {
  code: string;
  nativeLabel: string;
  labelKey: string;
}

export const LOCALE_STORAGE_KEY = "lotlocator:locale";

export const SUPPORTED_LOCALES: LocaleDefinition[] = [
  {
    code: "en-US",
    nativeLabel: "English (US)",
    labelKey: "settings.profile.preferences.language.options.en-US"
  },
  {
    code: "fil-PH",
    nativeLabel: "Filipino / Tagalog",
    labelKey: "settings.profile.preferences.language.options.fil-PH"
  }
];

export const DEFAULT_LOCALE = SUPPORTED_LOCALES[0].code;

const localeAliases = new Map<string, string>();
for (const locale of SUPPORTED_LOCALES) {
  localeAliases.set(locale.code.toLowerCase(), locale.code);
  localeAliases.set(locale.code.replace("-", "_").toLowerCase(), locale.code);
}
localeAliases.set("en", "en-US");
localeAliases.set("fil", "fil-PH");
localeAliases.set("tl", "fil-PH");
localeAliases.set("tl-ph", "fil-PH");

function resolveLocaleCandidate(input?: string | null): string | null {
  if (!input) {
    return null;
  }
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = trimmed.replace("_", "-").toLowerCase();
  if (localeAliases.has(normalized)) {
    return localeAliases.get(normalized)!;
  }
  const base = normalized.split("-")[0];
  if (base && localeAliases.has(base)) {
    return localeAliases.get(base)!;
  }
  return null;
}

export function normalizeLocale(input?: string | null): string {
  return resolveLocaleCandidate(input) ?? DEFAULT_LOCALE;
}

export function detectInitialLocale(): string {
  const stored = storage.get<string>(LOCALE_STORAGE_KEY);
  const storedMatch = resolveLocaleCandidate(stored);
  if (storedMatch) {
    return storedMatch;
  }
  if (typeof navigator !== "undefined") {
    const candidates: string[] = [];
    if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
      candidates.push(...navigator.languages.filter(Boolean));
    }
    if (navigator.language) {
      candidates.push(navigator.language);
    }
    for (const candidate of candidates) {
      const match = resolveLocaleCandidate(candidate);
      if (match) {
        return match;
      }
    }
  }
  return DEFAULT_LOCALE;
}

export function persistLocalePreference(locale: string) {
  try {
    storage.set(LOCALE_STORAGE_KEY, normalizeLocale(locale));
  } catch {
    // Ignore storage failures in restricted environments.
  }
}
